import { Component, effect, ElementRef, inject, input, OnInit, Renderer2, viewChild } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroSelectConfig } from '../../../../forms/fields/select/ziro-select.component';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import {
  HtmlCheckboxType,
  HtmlSwitchSize,
  ZiroCheckboxConfig,
} from '../../../../forms/fields/checkbox/ziro-checkbox.component';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, switchMap, take, tap } from 'rxjs';
import {
  AccountType,
  MicrosoftTeamsCallQueue,
  MicrosoftTeamsCallQueueViewEntityStatus,
  MicrosoftTeamsUserResult,
  RoutingMethod,
  VoiceAppViewEntity,
  VoiceAppViewEntityType,
} from '../../../../shared/models/generated/ziroModels';
import {
  MicrosoftTeamsUserSearchResource,
  TeamsUserSearchScope,
} from '../../../../helpdesk/shared/resources/microsoft-teams-user-search.resource';
import { TeamsCallQueuesContext } from '../../../../teams-call-queues/teams-call-queues.context';
import { LanguageOption } from '../../../../teams-call-queues/teams-call-queue/teams-call-queue-edit.component';
import { TranslateService } from '@ngx-translate/core';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../../../button/button.component';
import { getUserInitialsFromDisplayName } from '../../../../shared/utils';
import { Popover } from 'primeng/popover';

@Component({
  selector: 'ziro-ring-central-call-queue-teams-mapping-form',
  templateUrl: './ring-central-call-queue-teams-mapping-form.component.html',
  styleUrls: ['./ring-central-call-queue-teams-mapping-form.component.scss'],
  standalone: false,
  providers: [TeamsCallQueuesContext],
})
export class RingCentralCallQueueTeamsMappingFormComponent
  extends ZiroFormAbstractDirective<MicrosoftTeamsCallQueue>
  implements OnInit
{
  private _teamsCallQueuesContext = inject(TeamsCallQueuesContext);
  private _teamsUsersSearchResource = inject(MicrosoftTeamsUserSearchResource);
  private _translateService = inject(TranslateService);
  private _renderer2 = inject(Renderer2);

  readonly tenantId = input.required<string>();
  readonly languageOptions = input.required<LanguageOption[]>();

  resourceAccountsButton = viewChild<ElementRef<HTMLDivElement>>('resourceButton');
  resourceAccountsPopoverContainer = viewChild<ElementRef<HTMLDivElement>>('resourceAccountsPopoverContainer');
  resourceAccountsPopover = viewChild<Popover>('resourceAccountsPopover');

  formConfig: ZiroFormConfig;
  resourceAccountViews: VoiceAppViewEntity[] = [];
  isSearching = false;
  tooltipStyles: Record<string, string>;

  private _assignedResourceAccountGuids: string[] = [];
  private _allTenantCallQueues: MicrosoftTeamsCallQueue[] = [];
  private _teamsUserCandidates: MicrosoftTeamsUserResult[] = [];

  getUserInitialsFromDisplayName = getUserInitialsFromDisplayName;

  constructor() {
    super();
    effect(() => {
      this._setPopoverWidth(this.resourceAccountsButton(), this.resourceAccountsPopoverContainer());
    });
  }

  ngOnInit(): void {
    this._teamsCallQueuesContext.state$.pipe(take(1)).subscribe((callQueues) => {
      this._allTenantCallQueues = callQueues;
      this._assignedResourceAccountGuids = callQueues.flatMap((callQueue) => [
        ...callQueue.resourceAccountGuids,
        ...callQueue.callingIdResourceAccountGuids,
      ]);
    });

    this.formConfig = {
      fields: {
        name: {
          dataAutomation: 'teams-mapping-name',
          label: 'tkey;teams_call_queue.settings.name.label',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
          }),
          required: () => true,
          validation: [
            {
              validator: (val: string) =>
                val.length > 64 ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID,
              message: this._translateService.instant('tkey;validators.global.error.maxlength', { maxlength: 64 }),
            },
            {
              validator: (val: string) =>
                this._allTenantCallQueues.some((callQueue) => callQueue.name.toLowerCase() === val.toLowerCase())
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID,
              message: 'tkey;teams_call_queue.settings.name.unique.validation',
            },
          ],
        },
        language: {
          dataAutomation: 'teams-mapping-language',
          label: 'tkey;teams_call_queue.settings.language.label',
          componentConfig: new ZiroSelectConfig({
            options: this.languageOptions(),
            placeholder: 'tkey;teams_call_queue.settings.language.placeholder',
            bindValue: 'value',
          }),
          required: () => true,
        },
        routingMethod: {
          dataAutomation: 'teams-mapping-routing-method',
          label: 'tkey;teams_call_queue.settings.routing_method.label',
          componentConfig: new ZiroSelectConfig({
            options: [
              {
                label: `<i class="${ZiroIcons.ATTENDANT_ROUTING}"></i> ${this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.attendant.title',
                )}`,
                value: RoutingMethod.ATTENDANT,
                secondaryContent: this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.attendant.description',
                ),
              },
              {
                label: `<i class="${ZiroIcons.SERIAL_ROUTING}"></i> ${this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.serial.title',
                )}`,
                value: RoutingMethod.SERIAL,
                secondaryContent: this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.serial.description',
                ),
              },
              {
                label: `<i class="${ZiroIcons.ROUND_ROBIN_ROUTING}"></i> ${this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.round_robin.title',
                )}`,
                value: RoutingMethod.ROUND_ROBIN,
                secondaryContent: this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.round_robin.description',
                ),
              },
              {
                label: `<i class="${ZiroIcons.LONGEST_IDLE_TIME_ROUTING}"></i> ${this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.longest_idle_time.title',
                )}`,
                value: RoutingMethod.LONGEST_IDLE,
                secondaryContent: this._translateService.instant(
                  'tkey;teams_call_queue.settings.routing_method.longest_idle_time.description',
                ),
              },
            ],
            placeholder: 'tkey;teams_call_queue.settings.routing_method.placeholder',
            bindValue: 'value',
          }),
          required: () => true,
        },
        agentAlertTime: {
          dataAutomation: 'teams-mapping-agent-alert-time',
          label: 'tkey;teams_call_queue.settings.agent_alert_time.label',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
          }),
          validation: [
            {
              validator: (val) =>
                Number(val) < 15 || Number(val) > 180
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID,
              message: this._translateService.instant('tkey;validators.global.min_max.error', { min: 15, max: 180 }),
            },
          ],
        },
        presenceAwareRouting: {
          dataAutomation: 'teams-mapping-presence-aware-routing',
          label: 'tkey;teams_call_queue.settings.presence_aware_routing.label',
          componentConfig: new ZiroCheckboxConfig({
            checkboxType: HtmlCheckboxType.SWITCH,
            size: HtmlSwitchSize.LG,
            checkboxClass: 'ms-auto',
          }),
          disabled: () => this.formData.routingMethod === RoutingMethod.LONGEST_IDLE,
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.settings.presence_aware_routing.label.tooltip',
          disabledTooltip: 'tkey;teams_call_queue.settings.presence_aware_routing.disabled.tooltip',
        },
        allowOptOut: {
          dataAutomation: 'teams-mapping-allow-opt-out',
          label: 'tkey;teams_call_queue.settings.allow_opt_out.label',
          componentConfig: new ZiroCheckboxConfig({
            checkboxType: HtmlCheckboxType.SWITCH,
            size: HtmlSwitchSize.LG,
            checkboxClass: 'ms-auto',
          }),
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.settings.allow_opt_out.label.tooltip',
        },
      },
      options: {
        columnClasses: {
          label: 'col',
          input: 'col-2',
        },
        customClasses: 'ms-auto',
      },
    };
  }

  resourceAccountsSearch: (text$: Observable<string>) => Observable<MicrosoftTeamsUserResult[]> = (
    text$: Observable<string>,
  ): Observable<MicrosoftTeamsUserResult[]> =>
    text$.pipe(
      tap((term) => {
        if (term && term.length >= 3) {
          this.isSearching = true;
        }
      }),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 3) {
          return of([] as MicrosoftTeamsUserResult[]);
        }

        return this._teamsUsersSearchResource.search(
          this.tenantId(),
          TeamsUserSearchScope.CALL_QUEUE_MANAGEMENT,
          term.trim(),
          null,
          250,
          AccountType.RESOURCE_ACCOUNT,
        );
      }),
      map((teamsUsers) => {
        const selectedGuids = this.resourceAccountViews.map((view) => view.guid);
        const teamsUserCandidates = teamsUsers.filter((user) => !selectedGuids.includes(user.guid));
        this._teamsUserCandidates = teamsUserCandidates;

        return teamsUserCandidates.sort(
          (a, b) => Number(this.isResourceAccountDisabled(a)) - Number(this.isResourceAccountDisabled(b)),
        );
      }),
      tap(() => (this.isSearching = false)),
      catchError(() => of([] as MicrosoftTeamsUserResult[])),
    );

  resourceAccountFormatter: (userResult: MicrosoftTeamsUserResult) => string = (userResult) =>
    userResult.userPrincipalName;

  addResourceAccount(userResult: MicrosoftTeamsUserResult): void {
    const targetResourceAccount = this._teamsUserCandidates.find(
      (candidate) => candidate.userPrincipalName === userResult.userPrincipalName,
    );

    this.resourceAccountViews = [...this.resourceAccountViews, this._buildViewResourceAccount(targetResourceAccount)];

    this.entitySource.next({
      ...this.entity,
      resourceAccountGuids: [...this.entity.resourceAccountGuids, targetResourceAccount.guid],
    });

    this.resourceAccountsPopover().hide();
  }

  deleteResourceAccount(resourceAccount: VoiceAppViewEntity): void {
    this.resourceAccountViews = this.resourceAccountViews.filter((view) => view.guid !== resourceAccount.guid);

    this.entitySource.next({
      ...this.entity,
      resourceAccountGuids: this.entity.resourceAccountGuids.filter((guid) => guid !== resourceAccount.guid),
    });
  }

  onResourceAccountPopoverOpen(): void {
    this._setPopoverWidth(this.resourceAccountsButton(), this.resourceAccountsPopoverContainer());
    (
      document.querySelector('[data-automation="teams-mapping-resource-account-type-ahead"]') as HTMLInputElement
    )?.focus();
  }

  onPopoverClose(inputEl: HTMLInputElement): void {
    inputEl.value = '';
  }

  isResourceAccountDisabled(resourceAccount: MicrosoftTeamsUserResult): boolean {
    return (
      this._assignedResourceAccountGuids.includes(resourceAccount.guid) ||
      !resourceAccount.enterpriseVoiceEnabled ||
      !resourceAccount.lineUri
    );
  }

  preventClickOnDisabledOptions($event: PointerEvent, resourceAccount: MicrosoftTeamsUserResult): void {
    if (this.isResourceAccountDisabled(resourceAccount)) {
      $event.stopPropagation();
    }
  }

  getOptionTooltip(resourceAccount: MicrosoftTeamsUserResult): string {
    if (this._assignedResourceAccountGuids.includes(resourceAccount.guid)) {
      const callQueueName = this._allTenantCallQueues.find(
        (callQueue) =>
          callQueue.resourceAccountGuids.includes(resourceAccount.guid) ||
          callQueue.callingIdResourceAccountGuids.includes(resourceAccount.guid),
      )?.name;

      return this._translateService.instant(
        'tkey;teams_call_queue.resource_accounts.resource_search_results.already_assigned.tooltip',
        {
          voiceApp: callQueueName,
        },
      );
    } else if (!resourceAccount.enterpriseVoiceEnabled) {
      return this._translateService.instant(
        'tkey;teams_call_queue.resource_accounts.resource_search_results.enterprise_voice_disabled.tooltip',
      );
    } else if (!resourceAccount.lineUri) {
      return this._translateService.instant(
        'tkey;teams_call_queue.resource_accounts.resource_search_results.no_line_uri.tooltip',
      );
    }
    return '';
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  private _buildViewResourceAccount(userResult: MicrosoftTeamsUserResult): VoiceAppViewEntity {
    const {
      ref: { displayName },
      lineUri,
      lineUriExtension,
      userPrincipalName: upn,
      guid,
    } = userResult;

    return {
      type: VoiceAppViewEntityType.RESOURCE_ACCOUNT,
      description: '',
      displayName,
      lineUri,
      lineUriExtension,
      upn,
      guid,
      status: MicrosoftTeamsCallQueueViewEntityStatus.VALID,
    };
  }

  private _setPopoverWidth(buttonRef: ElementRef<HTMLDivElement>, popoverRef: ElementRef<HTMLDivElement>): void {
    const borderPixels = 2;
    const buttonWidth = buttonRef?.nativeElement?.offsetWidth ?? 0;
    if (!popoverRef || buttonWidth <= borderPixels) {
      return;
    }

    const width = `${buttonWidth - borderPixels}px`;
    this._renderer2.setStyle(popoverRef.nativeElement, 'min-width', width);
    this.tooltipStyles = { '--tooltip-width': width };
  }

  protected readonly ZiroIcons = ZiroIcons;
  protected readonly buttonStyles = ButtonStyles;
  protected readonly ZiroFormsValidationState = ZiroFormsValidationState;
}
